import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../lib/api';

const THEME_OPTIONS = [
  { value: 'nebula', label: 'Nebula (Default)' },
  { value: 'midnight', label: 'Midnight' },
  { value: 'sunrise', label: 'Sunrise' },
];

const REMINDER_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push (coming soon)' },
];

const WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const buildNotificationState = (user) => {
  const notifications = user?.settings?.notifications || {};
  return {
    dueRemindersEnabled: notifications.dueRemindersEnabled ?? true,
    dueReminderLeadHours:
      notifications.dueReminderLeadHours !== undefined ? notifications.dueReminderLeadHours : 24,
    dueReminderChannels:
      Array.isArray(notifications.dueReminderChannels) && notifications.dueReminderChannels.length
        ? notifications.dueReminderChannels
        : ['email'],
    weeklySummaryEnabled: notifications.weeklySummaryEnabled ?? true,
    weeklySummaryChannel: notifications.weeklySummaryChannel || 'email',
    weeklySummaryDay: notifications.weeklySummaryDay || 'monday',
  };
};

const formatWeekdayLabel = (isoDateString) => {
  if (!isoDateString) return '—';
  const date = new Date(`${isoDateString}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString(undefined, { weekday: 'short' });
};

const buildFormState = (user) => ({
  name: user?.name || '',
  title: user?.profile?.title || '',
  bio: user?.profile?.bio || '',
  location: user?.profile?.location || '',
  timezone: user?.settings?.timezone || 'UTC',
  theme: user?.settings?.theme || 'nebula',
  weeklyDigest: user?.settings?.weeklyDigest ?? true,
  notifications: buildNotificationState(user),
});

export default function Settings({ user, token, onUserUpdate, sendAnalytics, onLogout }) {
  const [form, setForm] = useState(buildFormState(user));
  const [status, setStatus] = useState({ message: '', error: '' });
  const [saving, setSaving] = useState(false);
  const [collaborations, setCollaborations] = useState({ sharedWith: [], sharedWithMe: [] });
  const [collabForm, setCollabForm] = useState({ email: '', role: 'viewer' });
  const [collabStatus, setCollabStatus] = useState({ message: '', error: '' });
  const [collabLoading, setCollabLoading] = useState(false);
  const [collabBusy, setCollabBusy] = useState(false);
  const [insights, setInsights] = useState({ loading: false, error: '', data: null });
  const [automationStatus, setAutomationStatus] = useState({ message: '', error: '' });
  const [automationBusy, setAutomationBusy] = useState(false);

  useEffect(() => {
    setForm(buildFormState(user));
  }, [user]);

  const fetchCollaborators = useCallback(async () => {
    if (!token) {
      setCollaborations({ sharedWith: [], sharedWithMe: [] });
      return;
    }

    setCollabLoading(true);
    setCollabStatus({ message: '', error: '' });

    try {
      const res = await fetch(apiUrl('/api/collaborators'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (!res.ok) {
        throw new Error('Unable to load collaborators.');
      }

      const body = await res.json();
      setCollaborations({
        sharedWith: body.sharedWith || [],
        sharedWithMe: body.sharedWithMe || [],
      });
    } catch (error) {
      setCollabStatus({ message: '', error: error.message });
    } finally {
      setCollabLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  const fetchInsights = useCallback(async () => {
    if (!token) {
      setInsights({ loading: false, error: '', data: null });
      return;
    }

    setInsights((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      const res = await fetch(apiUrl('/api/analytics/trends'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (!res.ok) {
        throw new Error('Unable to load insights.');
      }

      const body = await res.json();
      setInsights({ loading: false, error: '', data: body });
      sendAnalytics?.('insights_loaded', { streak: body.streakDays });
    } catch (error) {
      setInsights({ loading: false, error: error.message, data: null });
    }
  }, [token, onLogout, sendAnalytics]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue =
      type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : Number(value)) : value;

    if (name.startsWith('notifications.')) {
      const key = name.replace('notifications.', '');
      setForm((prev) => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          [key]: nextValue,
        },
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const toggleReminderChannel = (channel) => {
    setForm((prev) => {
      const current = prev.notifications?.dueReminderChannels || [];
      const exists = current.includes(channel);
      let nextChannels = current;

      if (exists) {
        if (current.length === 1) {
          return prev;
        }
        nextChannels = current.filter((value) => value !== channel);
      } else {
        nextChannels = [...current, channel];
      }

      return {
        ...prev,
        notifications: {
          ...prev.notifications,
          dueReminderChannels: nextChannels,
        },
      };
    });
  };

  const handleCollabInputChange = (event) => {
    const { name, value } = event.target;
    setCollabForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleInviteCollaborator = async (event) => {
    event.preventDefault();
    if (!token) {
      setCollabStatus({ message: '', error: 'You need to be logged in.' });
      return;
    }
    if (!collabForm.email.trim()) {
      setCollabStatus({ message: '', error: 'Invite email is required.' });
      return;
    }

    setCollabBusy(true);
    setCollabStatus({ message: '', error: '' });

    try {
      const res = await fetch(apiUrl('/api/collaborators'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: collabForm.email.trim(),
          role: collabForm.role,
        }),
      });

      const body = await res.json();

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (!res.ok) {
        throw new Error(body.error || 'Unable to send invite.');
      }

      setCollaborations((prev) => ({
        ...prev,
        sharedWith: [body, ...(prev.sharedWith || []).filter((entry) => entry.id !== body.id)],
      }));
      setCollabForm((prev) => ({ ...prev, email: '' }));
      setCollabStatus({ message: 'Invite sent.', error: '' });
      sendAnalytics?.('collaborator_added', { role: body.role });
    } catch (error) {
      setCollabStatus({ message: '', error: error.message });
    } finally {
      setCollabBusy(false);
    }
  };

  const handleRemoveCollaborator = async (collaborationId) => {
    if (!token) {
      setCollabStatus({ message: '', error: 'You need to be logged in.' });
      return;
    }

    if (typeof window !== 'undefined') {
      const confirmRemoval = window.confirm('Remove this collaborator?');
      if (!confirmRemoval) {
        return;
      }
    }

    setCollabBusy(true);
    setCollabStatus({ message: '', error: '' });

    try {
      const res = await fetch(apiUrl(`/api/collaborators/${collaborationId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (res.status !== 204) {
        const body = await res.json();
        throw new Error(body.error || 'Unable to remove collaborator.');
      }

      setCollaborations((prev) => ({
        ...prev,
        sharedWith: prev.sharedWith.filter((entry) => entry.id !== collaborationId),
      }));
      setCollabStatus({ message: 'Collaborator removed.', error: '' });
      sendAnalytics?.('collaborator_removed');
    } catch (error) {
      setCollabStatus({ message: '', error: error.message });
    } finally {
      setCollabBusy(false);
    }
  };

  const handleAutomationPreview = async (type) => {
    if (!token) {
      setAutomationStatus({ message: '', error: 'You need to be logged in.' });
      return;
    }

    setAutomationBusy(true);
    setAutomationStatus({ message: '', error: '' });

    try {
      const res = await fetch(apiUrl('/api/notifications/trigger'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ type }),
      });

      const body = await res.json();

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (!res.ok) {
        throw new Error(body.error || 'Unable to trigger notification.');
      }

      const message = body.message || (body.sent ? 'Notification sent.' : 'Preview generated.');
      setAutomationStatus({ message, error: '' });
      sendAnalytics?.('notification_preview', { type, sent: body.sent });
    } catch (error) {
      setAutomationStatus({ message: '', error: error.message });
    } finally {
      setAutomationBusy(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      setStatus({ message: '', error: 'You need to be logged in.' });
      return;
    }
    if (!form.name.trim()) {
      setStatus({ message: '', error: 'Name cannot be empty.' });
      return;
    }

    setSaving(true);
    setStatus({ message: '', error: '' });

    const notificationPayload = (() => {
      const lead = Number(form.notifications?.dueReminderLeadHours);
      const channels = Array.isArray(form.notifications?.dueReminderChannels)
        ? form.notifications.dueReminderChannels
        : ['email'];
      return {
        dueRemindersEnabled: Boolean(form.notifications?.dueRemindersEnabled),
        dueReminderLeadHours: Number.isFinite(lead) ? lead : 24,
        dueReminderChannels: channels.length ? channels : ['email'],
        weeklySummaryEnabled: Boolean(form.notifications?.weeklySummaryEnabled),
        weeklySummaryChannel: form.notifications?.weeklySummaryChannel || 'email',
        weeklySummaryDay: form.notifications?.weeklySummaryDay || 'monday',
      };
    })();

    const payload = {
      name: form.name.trim(),
      profile: {
        title: form.title,
        location: form.location,
        bio: form.bio,
      },
      settings: {
        timezone: form.timezone.trim() || 'UTC',
        theme: form.theme,
        weeklyDigest: form.weeklyDigest,
        notifications: notificationPayload,
      },
    };

    try {
      const res = await fetch(apiUrl('/api/me'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const body = await res.json();

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (!res.ok) {
        throw new Error(body.error || 'Unable to save preferences.');
      }

      onUserUpdate?.(body);
      sendAnalytics?.('settings_updated', { theme: body.settings?.theme });
      setStatus({ message: 'Preferences saved.', error: '' });
    } catch (error) {
      setStatus({ message: '', error: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <section className="settings-shell">
        <p className="status-line info">Loading profile...</p>
      </section>
    );
  }

  const created = user.createdAt ? new Date(user.createdAt).toLocaleString() : '—';

  const handleClearProfile = async () => {
    if (!token) return;
    if (typeof window !== 'undefined' && !window.confirm('Remove bio, title, and location?')) {
      return;
    }

    setSaving(true);
    setStatus({ message: '', error: '' });
    try {
      const res = await fetch(apiUrl('/api/me'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          profile: { bio: '', title: '', location: '' },
        }),
      });

      const body = await res.json();

      if (res.status === 401) {
        onLogout?.('Session expired. Please log in again.');
        return;
      }

      if (!res.ok) {
        throw new Error(body.error || 'Unable to remove profile details.');
      }

      onUserUpdate?.(body);
      setStatus({ message: 'Profile details removed.', error: '' });
    } catch (error) {
      setStatus({ message: '', error: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-shell">
      <header className="page-hero small">
        <p className="eyebrow">Account</p>
        <h1>Profile &amp; Settings</h1>
        <p>Keep your name, bio, and notification preferences up to date while we are in beta.</p>
      </header>

      <div className="settings-grid">
        <article className="settings-card">
          <h2>Profile</h2>
          <form className="settings-form" onSubmit={handleSubmit}>
            <label>
              Name
              <input type="text" name="name" value={form.name} onChange={handleInputChange} />
            </label>
            <label>
              Title
              <input type="text" name="title" value={form.title} onChange={handleInputChange} />
            </label>
            <label>
              Location
              <input type="text" name="location" value={form.location} onChange={handleInputChange} />
            </label>
            <label>
              Bio
              <textarea name="bio" rows="3" value={form.bio} onChange={handleInputChange} />
            </label>

            <div className="settings-field-grid">
              <label>
                Timezone
                <input type="text" name="timezone" value={form.timezone} onChange={handleInputChange} />
              </label>
              <label>
                Theme
                <select name="theme" value={form.theme} onChange={handleInputChange}>
                  {THEME_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="settings-toggle">
              <input
                type="checkbox"
                name="weeklyDigest"
                checked={form.weeklyDigest}
                onChange={handleInputChange}
              />
              Send me weekly digest emails
            </label>

            <div className="settings-actions">
              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save preferences'}
              </button>
              {status.message && <p className="status-line success">{status.message}</p>}
              {status.error && <p className="status-line error">{status.error}</p>}
            </div>
          </form>
        </article>

        <article className="settings-card">
          <h2>Account snapshot</h2>
          <dl className="account-meta">
            <div>
              <dt>Name</dt>
              <dd>{user.name || '—'}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt>Bio</dt>
              <dd>{user.profile?.bio || '—'}</dd>
            </div>
            <div>
              <dt>Title</dt>
              <dd>{user.profile?.title || '—'}</dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>{user.profile?.location || '—'}</dd>
            </div>
            <div>
              <dt>Timezone</dt>
              <dd>{user.settings?.timezone || 'UTC'}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{created}</dd>
            </div>
            <div>
              <dt>Digest emails</dt>
              <dd>{user.settings?.weeklyDigest ? 'Enabled' : 'Disabled'}</dd>
            </div>
          </dl>
          <button type="button" className="ghost" onClick={handleClearProfile} disabled={saving}>
            Remove profile details
          </button>
          <div className="settings-secondary-actions">
            <Link to="/" className="ghost-link">
              Back to product
            </Link>
            <button type="button" className="ghost" onClick={() => onLogout?.('Logged out.')}>Log out</button>
          </div>
        </article>

        <article className="settings-card notification-card">
          <h2>Notifications &amp; Automations</h2>
          <p className="section-intro">
            Choose how we nudge you about due dates or send weekly recaps to keep the streak alive.
          </p>
          <form className="automation-form" onSubmit={handleSubmit}>
            <section className="notification-section">
              <h3>Due-date reminders</h3>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  name="notifications.dueRemindersEnabled"
                  checked={Boolean(form.notifications?.dueRemindersEnabled)}
                  onChange={handleInputChange}
                />
                Notify me before tasks are due
              </label>
              <div className="settings-field-grid">
                <label>
                  Reminder window (hours)
                  <input
                    type="number"
                    min="1"
                    max="168"
                    name="notifications.dueReminderLeadHours"
                    value={form.notifications?.dueReminderLeadHours ?? ''}
                    onChange={handleInputChange}
                  />
                </label>
                <div className="channel-group">
                  <span>Channels</span>
                  <div className="channel-options">
                    {REMINDER_CHANNELS.map((channel) => (
                      <label key={channel.value}>
                        <input
                          type="checkbox"
                          checked={form.notifications?.dueReminderChannels?.includes(channel.value)}
                          onChange={() => toggleReminderChannel(channel.value)}
                        />
                        {channel.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <p className="help-text">We only remind you about todos with a due date.</p>
            </section>

            <section className="notification-section">
              <h3>Weekly summaries</h3>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  name="notifications.weeklySummaryEnabled"
                  checked={Boolean(form.notifications?.weeklySummaryEnabled)}
                  onChange={handleInputChange}
                />
                Send me a weekly recap
              </label>
              <div className="settings-field-grid">
                <label>
                  Delivery day
                  <select
                    name="notifications.weeklySummaryDay"
                    value={form.notifications?.weeklySummaryDay}
                    onChange={handleInputChange}
                  >
                    {WEEKDAY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Channel
                  <select
                    name="notifications.weeklySummaryChannel"
                    value={form.notifications?.weeklySummaryChannel}
                    onChange={handleInputChange}
                  >
                    <option value="email">Email</option>
                    <option value="push">Push (coming soon)</option>
                  </select>
                </label>
              </div>
            </section>

            <div className="settings-actions">
              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save notification settings'}
              </button>
              {status.message && <p className="status-line success">{status.message}</p>}
              {status.error && <p className="status-line error">{status.error}</p>}
            </div>
          </form>

          <div className="automation-actions">
            <button
              type="button"
              onClick={() => handleAutomationPreview('due_reminder')}
              disabled={automationBusy}
            >
              {automationBusy ? 'Working…' : 'Send reminder preview'}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => handleAutomationPreview('weekly_summary')}
              disabled={automationBusy}
            >
              {automationBusy ? 'Working…' : 'Send weekly summary'}
            </button>
          </div>
          {automationStatus.message && <p className="status-line success">{automationStatus.message}</p>}
          {automationStatus.error && <p className="status-line error">{automationStatus.error}</p>}
          {insights.data?.emailTransportReady ? (
            <p className="status-line success">Email delivery is configured.</p>
          ) : (
            <p className="status-line info">Add SMTP credentials to deliver emails outside the app.</p>
          )}
        </article>

        <article className="settings-card insights-card">
          <h2>Progress insights</h2>
          {insights.loading && <p className="status-line info">Crunching your data…</p>}
          {insights.error && <p className="status-line error">{insights.error}</p>}
          {insights.data ? (
            <>
              <div className="insights-grid">
                <div>
                  <p className="metric-label">Completion streak</p>
                  <p className="metric-value">{insights.data.streakDays ?? 0} days</p>
                </div>
                <div>
                  <p className="metric-label">Done this week</p>
                  <p className="metric-value">{insights.data.completedThisWeek ?? 0}</p>
                </div>
                <div>
                  <p className="metric-label">Upcoming (7d)</p>
                  <p className="metric-value">{insights.data.upcomingCount ?? 0}</p>
                </div>
                <div>
                  <p className="metric-label">Overdue</p>
                  <p className="metric-value">{insights.data.overdueCount ?? 0}</p>
                </div>
              </div>
              {insights.data.topTags?.length ? (
                <ul className="tag-pills">
                  {insights.data.topTags.map((entry) => (
                    <li key={entry.tag}>
                      <span>{entry.tag}</span>
                      <span className="pill-count">{entry.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="help-text">Complete tagged todos to surface focus trends.</p>
              )}
              <div className="sparkline-grid">
                {insights.data.completionSparkline?.map((point) => (
                  <div key={point.day}>
                    <p className="sparkline-day">{formatWeekdayLabel(point.day)}</p>
                    <p className="sparkline-count">{point.completed}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            !insights.loading && <p className="help-text">Complete a todo to unlock insights.</p>
          )}
          <div className="settings-actions">
            <button type="button" className="ghost" onClick={() => fetchInsights()} disabled={insights.loading}>
              {insights.loading ? 'Refreshing…' : 'Refresh insights'}
            </button>
          </div>
        </article>

        <article className="settings-card collaboration-card">
          <h2>Collaboration</h2>
          <p className="collab-intro">Invite teammates to view or edit your todo workspace.</p>
          <form className="collab-form" onSubmit={handleInviteCollaborator}>
            <label>
              Invite by email
              <input
                type="email"
                name="email"
                value={collabForm.email}
                onChange={handleCollabInputChange}
                placeholder="teammate@example.com"
              />
            </label>
            <label>
              Role
              <select name="role" value={collabForm.role} onChange={handleCollabInputChange}>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </label>
            <button type="submit" disabled={collabBusy}>
              {collabBusy ? 'Sending...' : 'Send invite'}
            </button>
          </form>
          {collabLoading && <p className="status-line info">Loading collaborators...</p>}
          {collabStatus.message && <p className="status-line success">{collabStatus.message}</p>}
          {collabStatus.error && <p className="status-line error">{collabStatus.error}</p>}
          <div className="collab-lists">
            <div className="collab-list">
              <h3>Shared with</h3>
              {collaborations.sharedWith.length === 0 ? (
                <p className="collab-empty">You have not invited anyone yet.</p>
              ) : (
                <ul>
                  {collaborations.sharedWith.map((entry) => (
                    <li key={entry.id} className="collab-item has-action">
                      <div className="collab-persona">
                        <div className="collab-heading">
                          <span className="collab-name">
                            {entry.collaborator?.name?.trim() || 'NULL'}
                          </span>
                          <span className={`collab-role collab-role-pill ${entry.role}`}>
                            {entry.role}
                          </span>
                        </div>
                        <span className="collab-email">{entry.collaborator?.email}</span>
                      </div>
                      <div className="collab-actions">
                        <button
                          type="button"
                          className="collab-remove"
                          onClick={() => handleRemoveCollaborator(entry.id)}
                          disabled={collabBusy}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="collab-list">
              <h3>Shared with me</h3>
              {collaborations.sharedWithMe.length === 0 ? (
                <p className="collab-empty">No teams have shared access with you yet.</p>
              ) : (
                <ul>
                  {collaborations.sharedWithMe.map((entry) => (
                    <li key={entry.id} className="collab-item has-action">
                      <div className="collab-persona">
                        <div className="collab-heading">
                          <span className="collab-name">
                            {entry.owner?.name?.trim() || 'NULL'}
                          </span>
                          <span className={`collab-role collab-role-pill ${entry.role}`}>
                            {entry.role}
                          </span>
                        </div>
                        <span className="collab-email">{entry.owner?.email}</span>
                      </div>
                      <div className="collab-actions muted">
                        <span className="collab-note">Shared workspace</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
